/**
 * tool-draft.js — «я вчера это делала, где оно?»
 *
 * A draft is the tool's own working state kept ON THE DEVICE: what the person
 * built or arranged AND, where the engine allows it, the source bytes
 * themselves. Nothing is sent anywhere: the store lives in this browser, on this
 * device, in this profile. That is the honest scope, and every consumer's copy
 * must say so instead of promising "saved".
 *
 * ── WHERE: the `work` storage, via work-storage.js ──────────────────────────
 * Drafts live where work-storage.js puts a person's work: in Chromium a
 * separate storage bucket `work` (persisted, strict durability), which is out
 * of reach of the eviction that empties the default bucket when the disk fills,
 * and whose writes reach the disk before they report success. Elsewhere they
 * live in the default IndexedDB with strict transactions. Drafts saved by the
 * previous version of this file (default storage) are moved over on first open.
 * Without work-storage.js on the page the store still works, in the default
 * storage.
 *
 * ── WHY IndexedDB and not the obvious alternatives ────────────────────────
 *   localStorage        strings only, ~5 MB, and NOT crash-safe: Chromium
 *                       commits it to disk about once a minute — a kill of the
 *                       browser 35 s after a write lost it 9 times out of 9,
 *                       while a completed IndexedDB write survived a kill 0 ms
 *                       later 6 times out of 6 (measured 29.09.2026, Chrome 153,
 *                       tests/scratch/storage-crash-probe-scratch.mjs)
 *   File System Access  showSaveFilePicker is Chromium-only, and asks for a
 *                       path on every save — wrong shape for an autosave
 *   IndexedDB           holds Blobs, quota is a share of free disk           ✔
 *
 * ── Bytes are an UPGRADE, state is the FLOOR ──────────────────────────────
 * WebKit has refused an ELEVEN-BYTE Blob in IndexedDB while accepting a 1 MB
 * ArrayBuffer in the same store (measured for /clone-effect, 2026) — that is
 * not a quota and not a size, it is Blob support. So the engine is asked by
 * WRITING one tiny blob rather than by sniffing a user-agent. When the answer
 * is no, bytes up to BYTES_FALLBACK_CAP are stored as ArrayBuffers instead (a
 * thumbnail's photos, not an hour of audio); above that, or when the quota
 * refuses, the draft still restores every setting and asks for the files back.
 *
 * ── What the engine does with a picked File ───────────────────────────────
 * Chromium stores a COPY of its bytes: a 3 MB file picked from disk, saved, and
 * then deleted (or overwritten) on disk came back from the draft with the
 * original SHA-256 (measured 29.09.2026). So a restored draft does not depend
 * on the person keeping their screenshots where they were.
 * The cost is small: a draft with 4.2 MB of photos saves in 20–28 ms, one
 * whose bytes did not change (only the state record is rewritten) in 1–2 ms.
 *
 * ── One transaction per save ──────────────────────────────────────────────
 * `slug::id`         — meta + state (kilobytes)
 * `slug::id::files`  — the bytes (megabytes)
 * Two records so that list() never pulls 400 MB off disk to render four rows,
 * and so that a slider nudge rewrites kilobytes, not the photos. They are
 * written in ONE transaction: a crash between them cannot leave a draft whose
 * state names photos that were never stored.
 *
 * ── Knowing that the browser took it ──────────────────────────────────────
 * Every successful save is noted in a small localStorage index together with
 * the EPOCH of the work storage (work-storage.js). If the storage is later
 * evicted, the next page finds a different epoch, and takeLost() names what was
 * lost, so the tool can say «the browser deleted it to free disk space»
 * instead of silently showing an empty editor.
 *
 * ── Two tabs ───────────────────────────────────────────────────────────────
 * A tool that edits ONE draft per page claims it (claim(id)). Two things then
 * keep two tabs from erasing each other's work:
 *   discovery — a BroadcastChannel ping: a live tab that holds the draft
 *               answers within milliseconds, so another tab does not reopen it
 *               (claimedElsewhere) and a late claimant is told to take a copy
 *               (opts.onConflict). Not Web Locks: a lock outlives its page by
 *               1.4 s after a navigation and 5.5 s after a close (measured
 *               29.09.2026, Chrome 153), so a RELOADED page found its own draft
 *               «busy» — exactly the moment a draft exists for.
 *   the write — a claimed draft is written only if nobody wrote it since this
 *               page last saw it; otherwise save() answers why:'foreign' and
 *               the tool forks. That also covers a tab the browser FROZE in the
 *               background: it cannot answer a ping, but it cannot sneak a stale
 *               write past this check either.
 *
 * Exposes window.__toolDraft.
 */
(function () {
'use strict';
if (window.__toolDraft) return;

var DB_NAME = 'timbrica_tool_drafts';
var DB_VERSION = 1;
var STORE = 'drafts';
var DEFAULT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
var DEFAULT_DEBOUNCE_MS = 1200;
/* Without Blob support the bytes travel as ArrayBuffers, read into memory in
   full — fine for a few photos, not for a queue of hour-long tracks. */
var BYTES_FALLBACK_CAP = 64 * 1024 * 1024;
var INDEX_KEY = 'tool-draft-index-v2';
var CHANNEL_PREFIX = 'tool-draft:';
/* how long a live tab gets to say «mine»: a BroadcastChannel hop is ~1 ms, a
   page busy with a cut-out may be slower — the write check covers the rest */
var PING_MS = 250;

/* ─────────────────────────────── plumbing ─────────────────────────────── */

var _dbP = null, _conn = null;
var _mode = '';   // 'b' work bucket, 'd' default storage
function upgrade(db) {
    if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
}
/* The work storage's own connection: the browser may close the database under the
   page (eviction, cleared site data), and every save after that used to fail until
   a reload — the connection reopens instead (work-storage.js, connection()). */
function conn() {
    var WS = window.__workStorage;
    if (!_conn && WS && typeof WS.connection === 'function') {
        _conn = WS.connection(DB_NAME, DB_VERSION, upgrade, {
            // a legacy record wins only if it is newer than the one already moved
            stamp: function (v) { return (v && v.savedAt) || 0; }
        });
    }
    return _conn;
}
/** A transaction on STORE: 'readwrite' (strict) or 'readonly'. Use it in the callback it is handed to. */
function txn(mode) {
    var c = conn();
    if (c) return c.tx(STORE, mode).then(function (t) { _mode = c.mode(); return t; });
    return open().then(function (db) { return mode === 'readonly' ? db.transaction(STORE, 'readonly') : writeTx(db); });
}
function open() {
    var c = conn();
    if (c) return c.db().then(function (db) { _mode = c.mode(); return db; });
    if (_dbP) return _dbP;
    var WS = window.__workStorage;
    if (WS && typeof WS.open === 'function') {
        _dbP = WS.open(DB_NAME, DB_VERSION, upgrade, {
            // a legacy record wins only if it is newer than the one already moved
            stamp: function (v) { return (v && v.savedAt) || 0; }
        }).then(function (r) { _mode = r.mode; return r.db; });
    } else {
        _dbP = new Promise(function (res, rej) {
            if (typeof indexedDB === 'undefined') { rej(new Error('no-idb')); return; }
            var r;
            // Private mode in some engines throws synchronously on open().
            try { r = indexedDB.open(DB_NAME, DB_VERSION); }
            catch (e) { rej(e); return; }
            r.onupgradeneeded = function () { upgrade(r.result); };
            r.onsuccess = function () { _mode = 'd'; res(r.result); };
            r.onerror = function () { rej(r.error || new Error('open')); };
            r.onblocked = function () { rej(new Error('blocked')); };
        });
    }
    _dbP.catch(function () { _dbP = null; });
    return _dbP;
}

function writeTx(db) {
    var WS = window.__workStorage;
    if (WS && WS.tx) return WS.tx(db, STORE);
    try { return db.transaction(STORE, 'readwrite', { durability: 'strict' }); }
    catch (_) { return db.transaction(STORE, 'readwrite'); }
}

/* Guarded transactions still waiting for their read — see pagehide in _bindHide. */
var _guardedTx = [];

/* entries: [[key, value]] — value undefined deletes the key. One transaction.
   [key, value, 'ifAbsent'] is written only if the key is not stored yet, checked in
   this same transaction: a file already kept under its content id is not copied
   again (18.10.2026: a save used to rewrite the bytes of EVERY file of a work each
   time its set changed — a 1.5 GB /video-editor project copied 1.5 GB per added
   clip, and one unreadable file failed them all).
   guard {key, rev}: write only if the record at `key` is not newer than `rev`
   (someone else saved it since) — else reject with name 'foreign'. */
function writeAll(entries, guard) {
    return txn('readwrite').then(function (tx) {
        return new Promise(function (res, rej) {
            var st = tx.objectStore(STORE), foreign = false, failed = null;
            var lazy = entries.some(function (e) { return e[2] === 'ifAbsent'; });
            /* A request that throws from a request's callback is not caught by anyone:
               the write is abandoned whole and the save answers with the reason. */
            var guarded = function (fn) {
                try { fn(); } catch (e) { failed = e; try { tx.abort(); } catch (_) {} }
            };
            var go = function () {
                entries.forEach(function (e) {
                    if (e[1] === undefined) { st.delete(e[0]); return; }
                    if (e[2] !== 'ifAbsent') { st.put(e[1], e[0]); return; }
                    var q = st.count(e[0]);
                    q.onsuccess = function () { guarded(function () { if (!q.result) st.put(e[1], e[0]); }); };
                });
            };
            var settle = function () { var i = _guardedTx.indexOf(tx); if (i !== -1) _guardedTx.splice(i, 1); };
            if (guard) {
                _guardedTx.push(tx);
                var g = st.get(guard.key);
                g.onsuccess = function () {
                    var cur = g.result;
                    if (cur && (cur.savedAt || 0) > guard.rev) { foreign = true; try { tx.abort(); } catch (_) {} return; }
                    guarded(go);
                };
            } else guarded(go);
            tx.oncomplete = function () { settle(); res(); };
            tx.onerror = function () { settle(); rej(tx.error || new Error('tx')); };
            tx.onabort = function () {
                settle();
                if (foreign) { var e = new Error('foreign'); e.name = 'foreign'; rej(e); return; }
                rej(failed || tx.error || new Error('abort'));
            };
            // hand the commit over now — a save issued from pagehide must not wait
            // (not with a guard or an «if absent»: their puts are issued from a read's callback)
            if (!guard && !lazy) { try { if (typeof tx.commit === 'function') tx.commit(); } catch (_) {} }
        });
    });
}
function put(key, value) { return writeAll([[key, value]]); }

function get(key) {
    return txn('readonly').then(function (tx) {
        return new Promise(function (res, rej) {
            var q = tx.objectStore(STORE).get(key);
            q.onsuccess = function () { res(q.result); };
            q.onerror = function () { rej(q.error); };
        });
    });
}

function del(key) { return writeAll([[key, undefined]]).catch(function () {}); }

/** Every meta record for one slug — the file blobs are NOT read here. */
function scanMeta(slug) {
    return txn('readonly').then(function (tx) {
        return new Promise(function (res, rej) {
            var out = [];
            var range;
            try { range = IDBKeyRange.bound(slug + '::', slug + '::\uffff'); }
            catch (e) { rej(e); return; }
            var cur = tx.objectStore(STORE).openCursor(range);
            cur.onsuccess = function () {
                var c = cur.result;
                if (!c) { res(out); return; }
                // Skip the companion files records — meta only.
                if (c.value && c.value.__kind === 'meta') out.push(c.value);
                c.continue();
            };
            cur.onerror = function () { rej(cur.error); };
        });
    });
}

/* ── can this engine hold a Blob at all? Answered by writing one. ── */
var _blobProbe = null;
function canStoreBlobs() {
    if (_blobProbe) return _blobProbe;
    _blobProbe = put('__probe', new Blob(['probe-blob']))
        .then(function () { return del('__probe').then(function () { return true; }); })
        .catch(function () { return false; });
    return _blobProbe;
}

/** Identity of a file the user might re-pick: name + size + mtime. */
function fileTag(f) {
    if (!f) return null;
    return {
        name: f.name || '', size: f.size || 0,
        type: f.type || '', lastModified: f.lastModified || 0
    };
}

function sameFile(tag, f) {
    return !!(tag && f && tag.name === f.name && tag.size === f.size
        && (!tag.lastModified || !f.lastModified || tag.lastModified === f.lastModified));
}

/** A File again, not a Blob — consumers read .name and feed upload pipelines. */
function reviveFile(blob, tag) {
    if (!blob) return null;
    tag = tag || {};
    try {
        return new File([blob], tag.name || 'file',
            { type: tag.type || blob.type || '', lastModified: tag.lastModified || Date.now() });
    } catch (_) { return blob; }
}

/* Which bytes are already stored is decided by OBJECT identity, not by
   name + size + mtime: two cut-outs of the same size are different pictures,
   and a Blob has neither a name nor a date. */
var _ids = typeof WeakMap === 'function' ? new WeakMap() : null, _idSeq = 0;
function objId(f) {
    if (!f) return '-';
    if (!_ids) return (f.name || '') + ':' + (f.size || 0) + ':' + (f.lastModified || 0);
    var v = _ids.get(f);
    if (!v) { v = 'o' + (++_idSeq); _ids.set(f, v); }
    return v;
}
function fingerprint(files) { return (files || []).map(objId).join('|'); }

/* ── one copy of a file, however many works hold it ──
   Chromium writes a separate copy of a Blob into EVERY record that holds it —
   even a Blob read back from the same database (measured 18.10.2026: the same
   20 MB file in two records = 40 MB on disk). A person making clips from one
   song kept one song per clip; report #9299 (/audio-to-video) had made ~40 and
   the list silently kept the newest 12, deleting the rest with their files. So
   the bytes live once, under their content id (`slug##blob::<cid>`), and a work
   holds only the ids. A key overwritten with the same bytes stays one copy
   (measured: five puts of 20 MB = 20 MB).
   The id is SHA-256 of the whole file up to CID_FULL_MAX (32 MB: ~150 ms on a
   desktop), above that of its size + three 2 MB samples — two different videos
   that agree in size and in all three samples do not occur in practice. Without
   crypto.subtle the id is random: no sharing, the old behaviour. */
var CID_FULL_MAX = 32 * 1024 * 1024, CID_SAMPLE = 2 * 1024 * 1024;
var _cids = typeof WeakMap === 'function' ? new WeakMap() : null;
function hex(buf) {
    var b = new Uint8Array(buf), out = '';
    for (var i = 0; i < b.length; i++) out += (b[i] < 16 ? '0' : '') + b[i].toString(16);
    return out;
}
function readBuf(blob) { return blob.arrayBuffer ? blob.arrayBuffer() : new Response(blob).arrayBuffer(); }
function randomCid() { return 'u' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10); }
function contentId(f) {
    if (_cids && _cids.has(f)) return _cids.get(f);
    var subtle = window.crypto && window.crypto.subtle;
    var size = (f && f.size) || 0;
    var p;
    if (!subtle || typeof subtle.digest !== 'function' || !f || typeof f.slice !== 'function') {
        p = Promise.resolve(randomCid());
    } else {
        var full = size <= CID_FULL_MAX;
        var mid = Math.max(0, Math.floor(size / 2) - CID_SAMPLE / 2);
        var parts = full ? [f] : [f.slice(0, CID_SAMPLE), f.slice(mid, mid + CID_SAMPLE), f.slice(size - CID_SAMPLE)];
        p = Promise.all(parts.map(readBuf)).then(function (bufs) {
            if (bufs.length === 1) return subtle.digest('SHA-256', bufs[0]);
            var total = 0; bufs.forEach(function (b) { total += b.byteLength; });
            var joined = new Uint8Array(total), at = 0;
            bufs.forEach(function (b) { joined.set(new Uint8Array(b), at); at += b.byteLength; });
            return subtle.digest('SHA-256', joined);
        }).then(function (d) { return (full ? 'f' : 's') + size.toString(36) + '.' + hex(d).slice(0, 40); });
    }
    p = p.catch(function () { return randomCid(); });
    if (_cids && f) _cids.set(f, p);
    return p;
}
function blobKey(slug, cid) { return slug + '##blob::' + cid; }

/* ── the loss index (localStorage; the evictor does not touch it) ── */
function readIndex() {
    try { return JSON.parse(localStorage.getItem(INDEX_KEY) || '{}') || {}; } catch (_) { return {}; }
}
function writeIndex(ix) {
    try {
        if (Object.keys(ix).length) localStorage.setItem(INDEX_KEY, JSON.stringify(ix));
        else localStorage.removeItem(INDEX_KEY);
    } catch (_) {}
}
function epochNow() {
    var WS = window.__workStorage;
    return WS && WS.epoch ? WS.epoch() : Promise.resolve(null);
}
/* One section per slug AND storage mode: a page that could not open the bucket
   writes to the default storage, which is another place, not a loss. */
function sectionKey(slug, ep) { return slug + '|' + ep.charAt(0); }
function capItems(items, n) {
    var ids = Object.keys(items);
    if (ids.length <= n) return;
    ids.sort(function (a, b) { return items[a].u - items[b].u; });
    ids.slice(0, ids.length - n).forEach(function (k) { delete items[k]; });
}
function indexNote(slug, id, meta) {
    return epochNow().then(function (ep) {
        if (!ep) return;
        var ix = readIndex(), k = sectionKey(slug, ep), s = ix[k];
        // The storage was replaced since these notes were written: what they name
        // is gone. Kept aside for takeLost(), which may run after this save.
        if (s && s.e !== ep) s = ix[k] = { e: ep, items: {}, lost: Object.assign({}, s.lost || {}, s.items) };
        if (!s) s = ix[k] = { e: ep, items: {} };
        s.items[id] = { u: meta.savedAt, l: String(meta.label || '').slice(0, 60) };
        capItems(s.items, 40);
        if (s.lost) capItems(s.lost, 40);
        writeIndex(ix);
    });
}
function indexDrop(slug, ids) {
    var ix = readIndex(), changed = false;
    Object.keys(ix).forEach(function (k) {
        if (k.indexOf(slug + '|') !== 0) return;
        ids.forEach(function (id) { if (ix[k].items && ix[k].items[id]) { delete ix[k].items[id]; changed = true; } });
        if (!Object.keys(ix[k].items || {}).length && !ix[k].lost) { delete ix[k]; changed = true; }
    });
    if (changed) writeIndex(ix);
}

function report(slug, f, data) {
    try { if (typeof window.__ae === 'function') window.__ae('feature_use', Object.assign({ slug: slug, f: f }, data || {})); } catch (_) {}
}

/* ─────────────────────────────── the store ─────────────────────────────── */

function Store(slug, opts) {
    opts = opts || {};
    this.slug = slug;
    this.maxAge = opts.maxAgeMs || DEFAULT_MAX_AGE_MS;
    this.debounceMs = opts.debounceMs || DEFAULT_DEBOUNCE_MS;
    this.maxDrafts = opts.maxDrafts || 0;       // 0 = no count cap
    this.bytesBudget = opts.bytesBudget || 0;   // 0 = no byte budget (see _trim)
    this.flushOnHide = opts.flushOnHide !== false;
    this._timer = 0;
    this._pending = null;   // {id, builder, done} of the scheduled save
    this._lastFiles = {};   // id → fingerprint of the bytes already stored
    this._lastRefs = {};    // id → content ids of those bytes (kept on meta-only saves)
    this._gcTimer = 0;
    this._held = {};        // id → {at, settled} of the drafts this page claimed
    this._rev = {};         // id → savedAt this page last wrote or read
    this._tab = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    this._bc = undefined;   // BroadcastChannel, lazily
    this._waiters = [];     // listeners for «mine» answers addressed to this page
    this.onConflict = typeof opts.onConflict === 'function' ? opts.onConflict : null;
    this._parked = null;    // ids let go on pagehide, taken back on a bfcache return
    this._inflight = null;  // {id, payload} of the newest guarded save not yet written
    this._queue = {};       // id → the tail of this page's saves of that draft (see save)
    this._unloading = false;
    this._hideBound = false;
}

Store.prototype._metaKey = function (id) { return this.slug + '::' + id; };
Store.prototype._filesKey = function (id) { return this.slug + '::' + id + '::files'; };

Store.prototype.canStoreBlobs = function () { return canStoreBlobs(); };

/**
 * Persist one draft.
 *
 * @param id      slot name ('auto' for the autosave, anything for named slots)
 * @param payload {state, files?, label?, preview?, meta?, v?}
 *                preview — a small image as a data: URL, for a list of drafts
 *                meta    — a few fields the list shows without loading the state
 * @param opts    {lastGasp} — the page is going away (see _bindHide)
 * @returns {ok, withFiles, degraded?, why?}
 */
/* This page's saves of ONE draft go one after another (01.10.2026, found on /drawing).
   A guarded save checks the stored record against the revision this page last saw;
   two in flight at once both carried the revision from BEFORE either wrote, so the
   second found the first one's write «newer» — refused as another tab's
   (why:'foreign'), and the controller forked a copy or dropped the newest state:
   «Новая работа», «Мои работы» or a row of the list clicked while the debounced save
   was still in flight lost the last change (a stroke at CPU ×40). Queued, each save
   starts once the one before it has written and knows its revision.
   The newest queued payload is what a page going away writes (pagehide re-saves
   _inflight), so a save still waiting in the queue is not lost with the tab; that
   last write itself is not queued — it has to leave in this task — and it is
   unguarded, so nothing can refuse it. A save stuck longer than QUEUE_WAIT_MS does
   not hold the next one hostage. */
var QUEUE_WAIT_MS = 8000;
function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
function nothing() {}
Store.prototype.save = function (id, payload, opts) {
    var self = this;
    if (opts && opts.lastGasp) return this._save(id, payload, opts, null);
    var job = (this._held[id] && payload && payload.state) ? (this._inflight = { id: id, payload: payload }) : null;
    var prev = this._queue[id];
    var run = function () { return self._save(id, payload, opts, job); };
    var p = prev ? Promise.race([prev, wait(QUEUE_WAIT_MS)]).then(run, run) : run();
    var tail = p.then(nothing, nothing);
    this._queue[id] = tail;
    tail.then(function () { if (self._queue[id] === tail) delete self._queue[id]; });
    return p;
};

Store.prototype._save = function (id, payload, opts, queuedJob) {
    var self = this;
    payload = payload || {};
    opts = opts || {};
    if (!payload.state) return Promise.resolve({ ok: false, why: 'empty' });

    var files = (payload.files || []).filter(Boolean);
    var meta = {
        __kind: 'meta',
        v: payload.v || 1,
        slug: this.slug,
        id: id,
        label: payload.label || '',
        savedAt: Date.now(),
        state: payload.state,
        preview: typeof payload.preview === 'string' ? payload.preview : '',
        meta: payload.meta || null,
        fileMeta: files.map(fileTag),
        bytes: files.reduce(function (s, f) { return s + (f.size || 0); }, 0),
        withFiles: false
    };
    var fp = fingerprint(files);

    // A claimed draft is written only if nobody wrote it since this page saw it —
    // except the last write of a page that is going away. The check needs a read
    // before the write, and the write is then issued from the read's callback: a
    // task an unloading page never gets. So F5 right after a change lost it
    // (stand C, tests/playwright/thumbnail-maker-design-survives-a-crash.mjs). A
    // page with a save still pending is the page that was being edited a moment
    // ago, so its last write goes out unconditionally, in the same task.
    var guard = (this._held[id] && !opts.lastGasp) ? { key: this._metaKey(id), rev: this._rev[id] || 0 } : null;
    // what this guarded save carries, in case the page goes away before its read
    // returns — registered when it was queued; a newer save since then owns _inflight
    var job = guard ? (queuedJob || (this._inflight = { id: id, payload: payload })) : null;

    return canStoreBlobs().then(function (blobsOk) {
        if (!files.length) {
            return writeAll([[self._metaKey(id), meta], [self._filesKey(id), undefined]], guard)
                .then(function () { delete self._lastFiles[id]; return { ok: true, withFiles: false }; });
        }
        meta.withFiles = true;
        // Bytes unchanged since the last write → leave the heavy record alone.
        if (self._lastFiles[id] === fp) {
            if (self._lastRefs[id]) meta.fileRefs = self._lastRefs[id];
            return writeAll([[self._metaKey(id), meta]], guard).then(function () { return { ok: true, withFiles: true }; });
        }
        return self._filesRecord(files, blobsOk, meta.savedAt, !!opts.lastGasp).then(function (rec) {
            if (!rec) return self._degrade(id, meta, { name: blobsOk ? 'too-big' : 'no-blob-support' }, guard);
            var entries = [[self._filesKey(id), rec.files], [self._metaKey(id), meta]].concat(rec.blobs);
            if (rec.files.refs) meta.fileRefs = rec.files.refs;
            return writeAll(entries, guard).then(function () {
                self._lastFiles[id] = fp;
                if (rec.files.refs) self._lastRefs[id] = rec.files.refs;
                self._gcSoon();   // the bytes this work no longer holds may be held by nobody
                return { ok: true, withFiles: true, bytesAs: rec.files.bufs ? 'buffer' : 'blob' };
            }, function (e) {
                // «Failed to write blobs»: a file whose bytes are gone — a pipeline's temporary
                // OPFS file deleted or rewritten after the File was taken (measured: exactly
                // then Chromium aborts with DataError). Until 18.10.2026 that dropped EVERY
                // file of the work (/video-editor: 1 139 such saves from 20 people since
                // 15.09). Now each file goes in alone; the ones that fail are left as missing
                // and the rest of the work keeps its bytes.
                if (!(e && e.name === 'DataError' && rec.files.refs && rec.blobs.length)) throw e;
                return self._saveFileByFile(id, meta, rec, guard, fp);
            });
        });
    }).catch(function (e) {
        if (e && e.name === 'foreign') return { ok: false, why: 'foreign' };
        // aborted on purpose by a page going away — its content went out as the last write
        if (self._unloading) return { ok: false, why: 'unloading' };
        // Quota said no (or the engine refused the bytes after all): keep the state.
        // ONLY then: dropping the bytes on any other error (an abort, a closed
        // connection) would delete photos that are safely stored.
        if (meta.withFiles && e && DEGRADE_ON[e.name]) return self._degrade(id, meta, e, guard);
        return { ok: false, why: (e && e.name) || 'error' };
    }).then(function (r) {
        if (job && self._inflight === job) self._inflight = null;
        if (r && r.ok) { self._rev[id] = meta.savedAt; self._afterSave(id, meta); }
        else if (r && r.why === 'foreign') self._conflict(id, 'write');
        return r;
    });
};

Store.prototype._saveFileByFile = function (id, meta, rec, guard, fp) {
    var self = this, bad = {}, chain = Promise.resolve();
    rec.blobs.forEach(function (b) {
        chain = chain.then(function () {
            return writeAll([b]).catch(function (err) {
                if (err && (err.name === 'QuotaExceededError')) throw err;   // no room: the degrade path answers
                bad[b[1].cid] = 1;
            });
        });
    });
    return chain.then(function () {
        var refs = rec.files.refs.map(function (c) { return bad[c] ? null : c; });
        var kept = refs.filter(Boolean);
        meta.fileRefs = refs;
        meta.withFiles = kept.length > 0;
        var files = { __kind: 'files', savedAt: rec.files.savedAt, refs: refs };
        return writeAll([[self._filesKey(id), meta.withFiles ? files : undefined], [self._metaKey(id), meta]], guard).then(function () {
            delete self._lastFiles[id];   // the next save tries the missing ones again
            self._lastRefs[id] = refs;
            self._gcSoon();
            report(self.slug, 'draft_blob_unreadable', { n: Object.keys(bad).length, of: rec.files.refs.length });
            return { ok: true, withFiles: meta.withFiles, missing: refs.length - kept.length };
        });
    });
};

/* The errors that mean «these bytes will not fit / cannot be stored here». */
var DEGRADE_ON = { QuotaExceededError: 1, DataCloneError: 1, DataError: 1, NotSupportedError: 1, UnknownError: 1 };

/* The bytes as stored: {files: the work's record, blobs: [[key, record]] to write
   with it}. Blobs where the engine takes them — each under its content id, shared
   by every work that holds the same file — else ArrayBuffers inline up to
   BYTES_FALLBACK_CAP, else null (the caller degrades to state only). */
Store.prototype._filesRecord = function (files, blobsOk, savedAt, lastGasp) {
    var self = this;
    /* The last write of a page going away must leave in THIS task (see _save): reading
       a file to learn its content id, and asking whether that id is stored, are tasks
       an unloading page never gets. So its files go into the work's record the old
       way, whole; the next ordinary save moves them under their content ids. */
    if (blobsOk && lastGasp) {
        return Promise.resolve({ files: { __kind: 'files', savedAt: savedAt, files: files }, blobs: [] });
    }
    if (blobsOk) {
        return Promise.all(files.map(contentId)).then(function (cids) {
            var seen = {}, blobs = [];
            cids.forEach(function (cid, i) {
                if (seen[cid]) return;
                seen[cid] = 1;
                // Written only if not stored yet — checked inside the work's own write
                // transaction (writeAll 'ifAbsent'), so another tab's clean-up cannot take
                // the record between the check and the write.
                blobs.push([blobKey(self.slug, cid), { __kind: 'blob', cid: cid, savedAt: savedAt, size: files[i].size || 0, blob: files[i] }, 'ifAbsent']);
            });
            return { files: { __kind: 'files', savedAt: savedAt, refs: cids }, blobs: blobs };
        });
    }
    var total = files.reduce(function (s, f) { return s + (f.size || 0); }, 0);
    if (total > BYTES_FALLBACK_CAP) return Promise.resolve(null);
    // An engine without Blobs in IndexedDB: the bytes go as ArrayBuffers, but still
    // under their content id — out of the work's record, so listing the works never
    // reads them, and a file shared by works is still stored once.
    return Promise.all(files.map(function (f) {
        return Promise.all([contentId(f), readBuf(f)]).then(function (r) { return { cid: r[0], buf: r[1], f: f }; });
    })).then(function (parts) {
        var seen = {}, blobs = [];
        parts.forEach(function (x) {
            if (seen[x.cid]) return;
            seen[x.cid] = 1;
            blobs.push([blobKey(self.slug, x.cid), { __kind: 'blob', cid: x.cid, savedAt: savedAt, size: x.f.size || 0, buf: x.buf, type: x.f.type || '' }, 'ifAbsent']);
        });
        return { files: { __kind: 'files', savedAt: savedAt, refs: parts.map(function (x) { return x.cid; }) }, blobs: blobs };
    });
};

/* Bytes no work holds any more are deleted — soon after a save or a removal, in
   ONE transaction that reads every work's ids and deletes the rest: a save from
   another tab cannot land between the read and the delete (transactions on the
   store run one after another), so a file it just started holding stays. */
Store.prototype._gcSoon = function () {
    var self = this;
    clearTimeout(this._gcTimer);
    this._gcTimer = setTimeout(function () { gcBlobs(self.slug); }, 1500);
};
function gcBlobs(slug) {
    return txn('readwrite').then(function (tx) {
        return new Promise(function (res) {
            var st, held = {}, dropped = 0;
            try { st = tx.objectStore(STORE); } catch (_) { res(-1); return; }
            var works = st.openCursor(IDBKeyRange.bound(slug + '::', slug + '::\uffff'));
            works.onsuccess = function () {
                var c = works.result;
                if (c) {
                    var v = c.value;
                    if (v && v.__kind === 'files' && v.refs) v.refs.forEach(function (r) { if (r) held[r] = 1; });
                    c.continue();
                    return;
                }
                var bytes = st.openCursor(IDBKeyRange.bound(slug + '##blob::', slug + '##blob::\uffff'));
                bytes.onsuccess = function () {
                    var d = bytes.result;
                    if (!d) return;
                    if (!(d.value && held[d.value.cid])) { d.delete(); dropped++; }
                    d.continue();
                };
            };
            tx.oncomplete = function () { res(dropped); };
            tx.onerror = tx.onabort = function () { res(-1); };
        });
    }).catch(function () { return -1; });
}

/**
 * The SETTINGS are the part worth keeping — drop the bytes, keep the draft, and
 * say it was degraded so the caller can tell the user the files will have to
 * be re-picked.
 */
Store.prototype._degrade = function (id, meta, err, guard) {
    delete this._lastFiles[id];
    delete this._lastRefs[id];
    this._gcSoon();
    meta.withFiles = false;
    return writeAll([[this._filesKey(id), undefined], [this._metaKey(id), meta]], guard).then(function () {
        return { ok: true, withFiles: false, degraded: (err && err.name) || 'error' };
    }).catch(function (e2) {
        return { ok: false, why: (e2 && e2.name) || 'error' };
    });
};

Store.prototype._afterSave = function (id, meta) {
    var self = this;
    indexNote(this.slug, id, meta).catch(function () {});
    var WS = window.__workStorage;
    if (WS && WS.protect) WS.protect(this.slug);
    if (this.maxDrafts > 0) this._prune(id);
    if (this.bytesBudget > 0) this._trim(id);
};

/* Every work stays; past the byte budget the OLDEST works stop holding their files.
   Report #9299 (/audio-to-video): ~60 clips from 58 different songs, 0.9 GB — and one
   person on the fleet would hold ~20 GB (122 songs, 5 400 photos). Most browsers here
   refuse the persisted bucket (1 399 sessions against 271 on a2v since 01.10), and a
   full disk then evicts the WHOLE bucket — every work at once, the open one included.
   So: newest first, works keep their bytes while the total (each shared file once)
   fits the budget; an older work over it keeps its state, name, preview and the list
   of its files (`fileMeta`), loses the bytes, and says so in the list and on opening
   («add the song again — everything else is here»). Never the work just saved, never
   one another tab has open. */
Store.prototype._trim = function (keepId) {
    var self = this;
    return Promise.all([scanMeta(this.slug), this.claimedElsewhere()]).then(function (r) {
        var rows = r[0].filter(function (m) { return m.withFiles; }), busy = r[1];
        rows.sort(function (a, b) { return (b.savedAt || 0) - (a.savedAt || 0); });
        var kept = {}, used = 0, drop = [], freed = 0;
        rows.forEach(function (m) {
            var refs = m.fileRefs, fm = m.fileMeta || [], add = 0, own = [];
            if (refs && refs.length === fm.length) {
                refs.forEach(function (c, i) { if (c && !kept[c] && own.indexOf(c) === -1) { own.push(c); add += (fm[i] && fm[i].size) || 0; } });
            } else add = m.bytes || 0;
            var mustKeep = m.id === keepId || busy[m.id] || self._held[m.id];
            if (!mustKeep && add > 0 && used + add > self.bytesBudget) { drop.push(m); freed += add; return; }
            own.forEach(function (c) { kept[c] = 1; });
            used += add;
        });
        if (!drop.length) return;
        return Promise.all(drop.map(function (m) {
            var meta = {}, k;
            for (k in m) meta[k] = m[k];
            meta.withFiles = false;
            meta.trimmed = Date.now();
            delete meta.fileRefs;
            return writeAll([[self._filesKey(m.id), undefined], [self._metaKey(m.id), meta]]).then(function () {
                delete self._lastFiles[m.id]; delete self._lastRefs[m.id];
            }, function () {});
        })).then(function () {
            self._gcSoon();
            report(self.slug, 'draft_bytes_trimmed', { n: drop.length, mb: Math.round(freed / 1048576), kept_mb: Math.round(used / 1048576) });
        });
    }).catch(function () {});
};

/* Keep the newest `maxDrafts`; never the one just saved, never one another tab has open. */
Store.prototype._prune = function (keepId) {
    var self = this;
    return Promise.all([scanMeta(this.slug), this.claimedElsewhere()]).then(function (r) {
        var rows = r[0], busy = r[1];
        if (rows.length <= self.maxDrafts) return;
        rows.sort(function (a, b) { return (b.savedAt || 0) - (a.savedAt || 0); });
        var drop = rows.slice(self.maxDrafts).filter(function (m) {
            return m.id !== keepId && !busy[m.id] && !self._held[m.id];
        });
        return Promise.all(drop.map(function (m) { return self.remove(m.id); }));
    }).catch(function () {});
};

/** Debounced autosave. `builder` is called at fire time, so it sees the latest state. */
Store.prototype.schedule = function (id, builder, done) {
    var self = this;
    clearTimeout(this._timer);
    this._pending = { id: id, builder: builder, done: done };
    this._bindHide();
    // warmed now, so that a save issued from pagehide finds both already answered
    canStoreBlobs();
    this._timer = setTimeout(function () { self.flush(); }, this.debounceMs);
};

/** Run the scheduled save now (e.g. before the page goes away). opts → save(). */
Store.prototype.flush = function (opts) {
    clearTimeout(this._timer); this._timer = 0;
    var p = this._pending;
    this._pending = null;
    if (!p) return Promise.resolve(null);
    var payload;
    try { payload = p.builder(); } catch (_) { return Promise.resolve(null); }
    if (!payload) return Promise.resolve(null);
    return this.save(p.id, payload, opts).then(function (r) {
        if (p.done) { try { p.done(r); } catch (_) {} }
        return r;
    }).catch(function () { return null; });
};

Store.prototype.cancelScheduled = function () { clearTimeout(this._timer); this._timer = 0; this._pending = null; };

/* A tab that is being hidden may never come back (closed, discarded, the
   machine switched off): a pending save goes out now, not after the debounce.
   On pagehide (reload, close, navigation — it comes before visibilitychange) the
   page has only the current task left: every step of that save up to put() and
   commit() is a resolved promise, so it is issued before the page is torn down,
   and it goes without the read-before-write check (see save()). */
Store.prototype._bindHide = function () {
    if (this._hideBound) return;
    this._hideBound = true;
    var self = this;
    try {
        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'hidden' && self.flushOnHide) self.flush();
        });
        window.addEventListener('pagehide', function () {
            // A guarded save already on its way waits for a read this page will
            // not get back — and every transaction after it queues behind it, the
            // last write included, and dies with it (F5 lost a change on prod,
            // 30.09, stand step C, 269 → 262). Abort it on purpose; if nothing newer
            // is pending, its own content goes out as the last write.
            var hadPending = !!self._pending, inflight = self._inflight;
            if (_guardedTx.length) {
                self._unloading = true;
                _guardedTx.slice().forEach(function (t) { try { t.abort(); } catch (_) {} });
            }
            if (self.flushOnHide) {
                self.flush({ lastGasp: true });
                if (!hadPending && inflight) self.save(inflight.id, inflight.payload, { lastGasp: true });
            }
            // A page that is going away stops answering for its drafts at once. On
            // F5 the page that replaces it asks «who has this draft?» while this one
            // is still being torn down, and an answer from here made the reloaded
            // page treat its OWN design as busy and not reopen it (prod, 30.09.2026,
            // tests/playwright/thumbnail-maker-design-survives-a-crash.mjs step C).
            self._parked = Object.keys(self._held).map(function (id) { return [id, self._held[id].prio || 2]; });
            self._held = {};
            if (self._bc) { try { self._bc.close(); } catch (_) {} }
            self._bc = undefined;
        });
        // back from the back/forward cache: the same page, the same drafts
        window.addEventListener('pageshow', function (e) {
            var ids = self._parked;
            self._parked = null;
            self._unloading = false;
            if (!e.persisted || !ids || !ids.length) return;
            ids.forEach(function (p) {
                var id = p[0];
                self.claim(id, p[1]).then(function (ok) { if (!ok) self._conflict(id, 'bfcache'); });
            });
        });
    } catch (_) {}
};

/** Meta rows for the picker, newest first. Expired drafts are dropped on sight. */
Store.prototype.list = function () {
    var self = this;
    return scanMeta(this.slug).then(function (rows) {
        var now = Date.now(), live = [], dead = [];
        rows.forEach(function (r) {
            if (now - (r.savedAt || 0) > self.maxAge) dead.push(r.id); else live.push(r);
        });
        dead.forEach(function (id) { self.remove(id); });
        live.sort(function (a, b) { return (b.savedAt || 0) - (a.savedAt || 0); });
        return live;
    }).catch(function () { return []; });
};

/** One draft with its files revived. Returns null when absent or expired. */
Store.prototype.load = function (id, opts) {
    var self = this, partial = !!(opts && opts.partial);
    var meta;
    return get(this._metaKey(id)).then(function (m) {
        meta = m;
        if (!meta || !meta.state) return null;
        if (Date.now() - (meta.savedAt || 0) > self.maxAge) { self.remove(id); meta = null; return null; }
        if (!meta.withFiles) return null;
        return get(self._filesKey(id)).then(function (rec) {
            if (!rec || !rec.refs) return rec;
            return Promise.all(rec.refs.map(function (cid) {
                if (!cid) return null;
                return get(blobKey(self.slug, cid)).then(function (b) {
                    if (b && b.blob) return b.blob;
                    if (b && b.buf) { try { return new Blob([b.buf], { type: b.type || '' }); } catch (_) { return null; } }
                    return null;
                }, function () { return null; });
            })).then(function (blobs) { return { refs: rec.refs, files: blobs }; });
        }).catch(function () { return null; });
    }).then(function (rec) {
        if (!meta || !meta.state) return null;
        var files = null;
        if (rec && rec.refs) self._lastRefs[id] = rec.refs;
        if (rec && rec.files && rec.files.length) {
            files = rec.files.map(function (b, i) { return reviveFile(b, (meta.fileMeta || [])[i]); });
        } else if (rec && rec.bufs && rec.bufs.length) {
            files = rec.bufs.map(function (b) {
                try { return new File([b.buf], b.name || 'file', { type: b.type || '', lastModified: b.lastModified || Date.now() }); }
                catch (_) { return new Blob([b.buf], { type: b.type || '' }); }
            });
        }
        // A tool that puts back what it can and asks for the rest (`partial`, /video-editor)
        // gets the files that came back in their places; the others get all or nothing.
        if (files && !partial && files.some(function (f) { return !f; })) files = null;
        if (files && partial && !files.some(Boolean)) files = null;
        // these very objects are what the tool will hand back on its next save
        if (files) self._lastFiles[id] = fingerprint(files);
        self._rev[id] = meta.savedAt || 0;
        return {
            id: meta.id, label: meta.label, savedAt: meta.savedAt,
            ageMs: Date.now() - (meta.savedAt || 0),
            state: meta.state, fileMeta: meta.fileMeta || [],
            preview: meta.preview || '', meta: meta.meta || null,
            files: files, withFiles: !!files,
            trimmed: !!meta.trimmed
        };
    }).catch(function () { return null; });
};

Store.prototype.remove = function (id) {
    var self = this;
    delete this._lastFiles[id];
    delete this._lastRefs[id];
    indexDrop(this.slug, [id]);
    return writeAll([[this._filesKey(id), undefined], [this._metaKey(id), undefined]]).catch(function () {})
        .then(function () { self._gcSoon(); });
};

/** The person's own budget (the list's «Место под файлы»): set it, and a smaller one
    applies at once — the oldest works over it stop holding their files. 0 = none. */
Store.prototype.setBudget = function (bytes, keepId) {
    this.bytesBudget = bytes > 0 ? bytes : 0;
    return this.bytesBudget > 0 ? (this._trim(keepId) || Promise.resolve()) : Promise.resolve();
};

/** What the works of this slug take in this browser: {works, bytes} — each shared
    file counted once (bytes of works saved before content ids: their own sum). */
Store.prototype.usage = function () {
    return this.list().then(function (rows) {
        var seen = {}, bytes = 0;
        rows.forEach(function (r) {
            if (!r.withFiles) return;
            var refs = r.fileRefs, fm = r.fileMeta || [];
            if (!refs || refs.length !== fm.length) { bytes += r.bytes || 0; return; }
            refs.forEach(function (cid, i) {
                if (!cid || seen[cid]) return;
                seen[cid] = 1;
                bytes += (fm[i] && fm[i].size) || 0;
            });
        });
        return { works: rows.length, bytes: bytes };
    });
};

Store.prototype.clearAll = function () {
    var self = this;
    return this.list().then(function (rows) {
        return Promise.all(rows.map(function (r) { return self.remove(r.id); }));
    });
};

/**
 * Drafts of this slug that the BROWSER removed since they were saved (an
 * eviction, or site data cleared elsewhere): [{id, label, savedAt}]. Answered
 * once; the index then starts over for the current storage.
 */
Store.prototype.takeLost = function () {
    var slug = this.slug;
    return epochNow().then(function (ep) {
        if (!ep) return [];
        var ix = readIndex(), k = sectionKey(slug, ep), s = ix[k];
        if (!s) return [];
        var gone = Object.assign({}, s.lost || {});
        if (s.e !== ep) { Object.assign(gone, s.items); s.items = {}; s.e = ep; }
        delete s.lost;
        if (!Object.keys(s.items).length) delete ix[k];
        writeIndex(ix);
        var lost = Object.keys(gone).map(function (id) {
            return { id: id, label: gone[id].l || '', savedAt: gone[id].u || 0 };
        });
        if (lost.length) {
            var newest = lost.reduce(function (m, x) { return Math.max(m, x.savedAt); }, 0);
            report(slug, 'draft_store_lost', { n: lost.length, age_h: Math.round((Date.now() - newest) / 3600000) });
        }
        return lost;
    }).catch(function () { return []; });
};

/* ── two tabs: who has which draft open ── */
Store.prototype._chan = function () {
    if (this._bc !== undefined) return this._bc;
    var self = this;
    this._bc = null;
    try {
        if (typeof BroadcastChannel !== 'function') return null;
        this._bc = new BroadcastChannel(CHANNEL_PREFIX + this.slug);
        this._bc.onmessage = function (ev) {
            var m = ev.data || {};
            if (m.tab === self._tab) return;
            if (m.t === 'who') {
                var ids = Object.keys(self._held);
                if (ids.length) self._bc.postMessage({ t: 'mine', ids: ids, prio: self._prios(ids), tab: self._tab, to: m.tab });
            } else if (m.t === 'claim') {
                var mine = self._held[m.id];
                if (!mine) return;
                // The stronger claim keeps the draft: the tab that HAD it open (or the
                // person picked it) beats a tab that merely reopened it on arrival —
                // else whichever tab finished loading first won, and a reloaded tab
                // could lose its own design to a tab opened a second later (prod,
                // 30.09). Equal strength: the earlier claim, then the smaller tab id.
                var mp = mine.prio || 1, tp = m.prio || 1;
                var keep = mp > tp || (mp === tp && (mine.at < m.at || (mine.at === m.at && self._tab < m.tab)));
                if (keep) self._bc.postMessage({ t: 'mine', ids: [m.id], prio: self._prios([m.id]), tab: self._tab, to: m.tab });
                else self._conflict(m.id, 'outranked');
            } else if (m.t === 'mine' && m.to === self._tab) {
                self._waiters.slice().forEach(function (w) { try { w(m); } catch (_) {} });
                // an answer that arrives after the claim settled: this page lost it
                (m.ids || []).forEach(function (id) {
                    if (self._held[id] && self._held[id].settled) self._conflict(id, 'late');
                });
            }
        };
    } catch (_) { this._bc = null; }
    return this._bc;
};

Store.prototype._prios = function (ids) {
    var self = this, out = {};
    ids.forEach(function (id) { if (self._held[id]) out[id] = self._held[id].prio || 1; });
    return out;
};

Store.prototype._conflict = function (id, how) {
    delete this._held[id];
    report(this.slug, 'draft_conflict', { how: how });
    if (this.onConflict) { try { this.onConflict(id, how); } catch (_) {} }
};

/* Post a message and collect the «mine» answers addressed to this page for `ms`. */
Store.prototype._ask = function (msg, ms) {
    var self = this, bc = this._chan(), got = [];
    if (!bc) return Promise.resolve(got);
    return new Promise(function (resolve) {
        var w = function (m) { got.push(m); };
        self._waiters.push(w);
        try { bc.postMessage(msg); } catch (_) {}
        setTimeout(function () {
            self._waiters = self._waiters.filter(function (x) { return x !== w; });
            resolve(got);
        }, ms || PING_MS);
    });
};

/**
 * This page edits draft `id` from now on. → Promise<boolean>: false when a live
 * tab already has it open with an equal or stronger claim — the page must then
 * take a copy under a new id (or, if nothing of its own went in, let it go).
 * `prio` — the claim's strength: 2 (the default) = this tab had it open or the
 * person picked it; 1 = reopened on arrival by a tab that never had it. A
 * stronger claim arriving later takes the draft away (onConflict 'outranked').
 * Without BroadcastChannel every claim succeeds (the write check still holds).
 */
Store.prototype.claim = function (id, prio) {
    var self = this;
    if (this._held[id]) return Promise.resolve(true);
    this._bindHide();      // a claim is let go when the page goes away
    var at = Date.now();
    prio = prio || 2;
    this._held[id] = { at: at, prio: prio, settled: false };
    return this._ask({ t: 'claim', id: id, tab: this._tab, at: at, prio: prio }).then(function (answers) {
        var taken = answers.some(function (m) { return (m.ids || []).indexOf(id) !== -1; });
        if (taken) { delete self._held[id]; return false; }
        if (self._held[id]) self._held[id].settled = true;
        return true;
    });
};

Store.prototype.release = function (id) { delete this._held[id]; };

/**
 * → Promise<{id: prio}> of the drafts of this slug another live tab has open,
 * with the strength of that tab's claim (see claim()); truthy either way.
 * `ms` — how long to listen. A tab that is not in front answers when the
 * browser lets it run — seconds later on a loaded machine: a decision that can
 * wait — «reopen the unfinished design?» — should ask once the page is idle and
 * listen longer than the default, and still expect a late «mine».
 */
Store.prototype.claimedElsewhere = function (ms) {
    return this._ask({ t: 'who', tab: this._tab }, ms).then(function (answers) {
        var out = {};
        answers.forEach(function (m) {
            (m.ids || []).forEach(function (id) {
                var p = (m.prio && m.prio[id]) || 1;
                out[id] = Math.max(out[id] || 0, p);
            });
        });
        return out;
    });
};

/**
 * «Which drafts are open in another tab?» for as long as the caller shows the
 * answer — an open list of drafts, say. Asks once, then calls cb(id, prio) for
 * every answer until the returned stop() is called (or `ms` passes, if given).
 * No fixed window is long enough: a tab in the background answers when the
 * browser lets it run, and on a loaded machine its main thread stood still for
 * 2–7 s, once 18 s (30.09.2026: answers 2.8 s typical, 13.5 s at worst).
 */
Store.prototype.watchElsewhere = function (cb, ms) {
    var self = this, bc = this._chan(), t = 0;
    if (!bc) return function () {};
    var w = function (m) {
        (m.ids || []).forEach(function (id) { try { cb(id, (m.prio && m.prio[id]) || 1); } catch (_) {} });
    };
    var stop = function () { self._waiters = self._waiters.filter(function (x) { return x !== w; }); if (t) clearTimeout(t); };
    this._waiters.push(w);
    try { bc.postMessage({ t: 'who', tab: this._tab }); } catch (_) {}
    if (ms) t = setTimeout(stop, ms);
    return stop;
};

/**
 * Ask the browser to keep this data out of the eviction pool. In the `work`
 * bucket that happens by itself after a save; this is for the default storage,
 * and only worth calling on an EXPLICIT save — Firefox surfaces a permission
 * prompt, and a prompt nobody asked for is worse than eviction nobody hits.
 */
Store.prototype.requestPersistence = function () {
    try {
        if (navigator.storage && navigator.storage.persist) return navigator.storage.persist();
    } catch (_) {}
    return Promise.resolve(false);
};

/** {usage, quota} in bytes, or null when the engine will not say. */
Store.prototype.estimate = function () {
    try {
        if (navigator.storage && navigator.storage.estimate) return window.__storageEstimate();
    } catch (_) {}
    return Promise.resolve(null);
};

/** Where the drafts live on this page: {mode: 'b'|'d'|''}. */
Store.prototype.where = function () {
    return open().then(function () { return { mode: _mode }; }, function () { return { mode: '' }; });
};

window.__toolDraft = {
    store: function (slug, opts) { return new Store(slug, opts); },
    canStoreBlobs: canStoreBlobs,
    sameFile: sameFile,
    fileTag: fileTag,
    reviveFile: reviveFile,
    DEFAULT_MAX_AGE_MS: DEFAULT_MAX_AGE_MS,
    _contentId: contentId,   // exported for tests
    _gcBlobs: gcBlobs,       // exported for tests
    _raw: {                  // exported for tests: legacy records, what bytes are stored
        get: get,
        put: put,
        blobKeys: function (slug) {
            return txn('readonly').then(function (tx) {
                return new Promise(function (res, rej) {
                    var out = [];
                    var c = tx.objectStore(STORE).openKeyCursor(IDBKeyRange.bound(slug + '##blob::', slug + '##blob::\uffff'));
                    c.onsuccess = function () { var k = c.result; if (!k) { res(out); return; } out.push(k.key); k.continue(); };
                    c.onerror = function () { rej(c.error); };
                });
            });
        }
    },
    BYTES_FALLBACK_CAP: BYTES_FALLBACK_CAP
};
})();
